import api from './api';

// TypeScript interfaces
export interface CaseStudySection {
  id: string;
  title: string;
  icon: string;
  content: string[];
  order: number;
  lastUpdated?: Date;
}

export interface Stakeholder {
  name: string;
  role: string;
  contribution: string;
}

export interface OutcomeMetric {
  category: 'Business' | 'Technical' | 'Customer' | 'Operational' | 'Strategic';
  metric: string;
  baseline?: string;
  target?: string;
  achieved?: string;
}

export interface CapstoneObjective {
  id: string;
  category: 'Analytics' | 'Governance' | 'Leadership' | 'Innovation' | 'Strategy';
  objective: string;
  dhsAlignment: string;
  activities: string[];
  outcomes: string[];
}

export interface MaturityStage {
  stage: string;
  description: string;
  activities: string[];
}

export interface GovernancePillar {
  pillar: string;
  description: string;
  components: string[];
}

export interface TestAndLearnStrategy {
  strategy: string;
  description: string;
  implementation: string[];
}

export interface DHSAlignment {
  missionAlignment?: string;
  analyticsMaturityRoadmap?: {
    overview: string;
    stages: MaturityStage[];
    keyPrinciples: string[];
  };
  governanceFramework?: {
    overview: string;
    pillars: GovernancePillar[];
  };
  pilotProgramApproach?: {
    overview: string;
    currentPilots: string[];
    testAndLearnStrategies: TestAndLearnStrategy[];
    successFactors: string[];
  };
  securityConsiderations?: string[];
  complianceFrameworks?: string[];
  governmentApplications?: string[];
}

export interface Reference {
  title: string;
  url: string;
  type: 'Article' | 'Case Study' | 'Video' | 'Report' | 'Academic';
}

export interface CaseStudy {
  _id?: string;
  caseStudyId: string;
  title: string;
  organization: string;
  industry: string;
  region: string;
  overview: string;
  sections: CaseStudySection[];
  stakeholders: Stakeholder[];
  outcomes: OutcomeMetric[];
  capstoneObjectives: CapstoneObjective[];
  dhsAlignment: DHSAlignment;
  references: Reference[];
  status: 'Draft' | 'Published' | 'Archived';
  version: string;
  createdBy?: string;
  lastModifiedBy?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

// API service functions
const caseStudyService = {
  // Get all case studies
  async getAllCaseStudies(): Promise<CaseStudy[]> {
    try {
      const response = await api.get('/case-studies');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching case studies:', error);
      throw error;
    }
  },

  // Get single case study by ID
  async getCaseStudy(caseStudyId: string): Promise<CaseStudy> {
    try {
      const response = await api.get(`/case-studies/${caseStudyId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching case study:', error);
      throw error;
    }
  },

  // Create new case study
  async createCaseStudy(caseStudy: Partial<CaseStudy>): Promise<CaseStudy> {
    try {
      const response = await api.post('/case-studies', caseStudy);
      return response.data.data;
    } catch (error) {
      console.error('Error creating case study:', error);
      throw error;
    }
  },

  // Update entire case study
  async updateCaseStudy(caseStudyId: string, updates: Partial<CaseStudy>): Promise<CaseStudy> {
    try {
      const response = await api.put(`/case-studies/${caseStudyId}`, updates);
      return response.data.data;
    } catch (error) {
      console.error('Error updating case study:', error);
      throw error;
    }
  },

  // Update specific section
  async updateSection(caseStudyId: string, sectionId: string, sectionData: Partial<CaseStudySection>): Promise<CaseStudy> {
    try {
      const response = await api.put(`/case-studies/${caseStudyId}/section/${sectionId}`, sectionData);
      return response.data.data;
    } catch (error) {
      console.error('Error updating section:', error);
      throw error;
    }
  },

  // Update stakeholders
  async updateStakeholders(caseStudyId: string, stakeholders: Stakeholder[]): Promise<CaseStudy> {
    try {
      const response = await api.put(`/case-studies/${caseStudyId}/stakeholders`, { stakeholders });
      return response.data.data;
    } catch (error) {
      console.error('Error updating stakeholders:', error);
      throw error;
    }
  },

  // Update outcomes
  async updateOutcomes(caseStudyId: string, outcomes: OutcomeMetric[]): Promise<CaseStudy> {
    try {
      const response = await api.put(`/case-studies/${caseStudyId}/outcomes`, { outcomes });
      return response.data.data;
    } catch (error) {
      console.error('Error updating outcomes:', error);
      throw error;
    }
  },

  // Update capstone objectives
  async updateCapstoneObjectives(caseStudyId: string, capstoneObjectives: CapstoneObjective[]): Promise<CaseStudy> {
    try {
      const response = await api.put(`/case-studies/${caseStudyId}/capstone`, { capstoneObjectives });
      return response.data.data;
    } catch (error) {
      console.error('Error updating capstone objectives:', error);
      throw error;
    }
  },

  // Update DHS alignment
  async updateDHSAlignment(caseStudyId: string, dhsAlignment: DHSAlignment): Promise<CaseStudy> {
    try {
      const response = await api.put(`/case-studies/${caseStudyId}/dhs-alignment`, { dhsAlignment });
      return response.data.data;
    } catch (error) {
      console.error('Error updating DHS alignment:', error);
      throw error;
    }
  },

  // Delete case study
  async deleteCaseStudy(caseStudyId: string): Promise<void> {
    try {
      await api.delete(`/case-studies/${caseStudyId}`);
    } catch (error) {
      console.error('Error deleting case study:', error);
      throw error;
    }
  }
};

export default caseStudyService;
